/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {AdminPageTitle} from "../containers/pages/admin/constants";
import {sensorRadarTitle} from "../containers/pages/radar/SensorRadar/constants";
import {miscellaneousObjectsTitle, objectClassificationTitle, objectDefinitionsTitle, picklistDataTitle, potentiallyMovingObjectsTitle, signObjectsTitle} from "../containers/pages/objectApp/constants";
import {sensorCameraHeaderTitle} from "../containers/pages/camera/constants";
import {VEHICLE_PROGRAMS_CONSTANTS, vehicleProgramsTitle} from "../containers/pages/vehiclePrograms/constants";
import {dashboardTitle} from "../containers/pages/navigation/constants";
import {SCENARIO_ANALYSIS_CONSTANTS} from "../containers/pages/ScenarioAnalysis/constants";
import {coverageRequirementsHeaderTitle, coverageRequirementsLandingRoute,} from "../containers/pages/CoverageRequirements/constants";
import ADAS_PROGRAMS_CONSTANTS from "../containers/pages/AdasPrograms/constants";
import {sensorLidarHeaderTitle} from "../containers/pages/lidar/constants";
import {sensorUltrasonicHeaderTitle} from "../containers/pages/ultrasonic/constants";
import {SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS} from "../containers/pages/SensingSystemArchitectureDesign/constants";
import {ROAD_ANALYSIS_CONSTANTS} from "../containers/pages/RoadAnalysis/constants";

const ARGOS_ROUTES = [
  {
    path: '/',
    sidebar: true,
    headerTitle: dashboardTitle
  },
  {
    path: '/login',
    sidebar: true,
  },
  {
    path: '/landing',
    sidebar: true,
    headerTitle: dashboardTitle
  },
  {
    path: '/admin',
    sidebar: true,
    headerTitle: AdminPageTitle
  },
  {
    path: '/objects',
    sidebar: true,
    headerTitle: objectDefinitionsTitle
  },
  {
    path: '/potentiallymovingobjects',
    sidebar: true,
    headerTitle: potentiallyMovingObjectsTitle
  },
  {
    path: '/objectclassification',
    sidebar: true,
    headerTitle: objectClassificationTitle
  },
  {
    path: '/signspicklist',
    sidebar: true,
    headerTitle: picklistDataTitle
  },
  {
    path: '/signs',
    sidebar: true,
    headerTitle: signObjectsTitle
  },
  {
    path: '/miscellaneousObjects',
    sidebar: true,
    headerTitle: miscellaneousObjectsTitle
  },
  {
    path: '/sensorradar',
    sidebar: true,
    headerTitle: sensorRadarTitle
  },
  {
    path: '/sensorcamera',
    sidebar: true,
    headerTitle: sensorCameraHeaderTitle
  },
  {
    path: '/sensorlidar',
    sidebar: true,
    headerTitle: sensorLidarHeaderTitle
  },
  {
    path: '/lidarPerformance',
    sidebar: false,
  },
  {
    path: '/sensorultrasonic',
    sidebar: true,
    headerTitle: sensorUltrasonicHeaderTitle
  },
  {
    path: '/radarPerformance',
    sidebar: false,
  },
  {
    path: '/sensordescription',
    sidebar: false,
  },
  {
    path: '/ultrasonicPerformance',
    sidebar: false,
  },
  {
    path: '/sensorultrasonicdescription',
    sidebar: false,
  },
  {
    path: VEHICLE_PROGRAMS_CONSTANTS.itemDetails.route,
    sidebar: false,
  },
  {
    path: '/cameraPerformance',
    sidebar: false,
  },
  {
    path: '/cameradescription',
    sidebar: false,
  },
  {
    path: '/vehicleprogram',
    sidebar: true,
    headerTitle: vehicleProgramsTitle
  },
  {
    path: '/InfrastructureTestFunction',
    sidebar: true,
  },
  {
    path: '/addvehicleprogram',
    sidebar: false,
  },
  {
    path: '/objectDetails',
    sidebar: false,
  },
  {
    path: SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.landing.route,
    sidebar: true,
    headerTitle: SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.landing.title,
  },
  {
    path: SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.route,
    sidebar: false,
  },
  {
    path: SCENARIO_ANALYSIS_CONSTANTS.landing.route,
    sidebar: true,
    headerTitle: SCENARIO_ANALYSIS_CONSTANTS.landing.title,
  },
  {
    path: SCENARIO_ANALYSIS_CONSTANTS.viewModel.route,
    sidebar: false,
  },
  {
    path: coverageRequirementsLandingRoute,
    sidebar: true,
    headerTitle: coverageRequirementsHeaderTitle
  },
  {
    path: '/requestaccess',
    sidebar: false
  },
  {
    path: ADAS_PROGRAMS_CONSTANTS.mainRoute,
    headerTitle: ADAS_PROGRAMS_CONSTANTS.sidebar.title,
    sidebar: true
  },
  {
    path: '/cameraPerformance',
  },
  // Road Analysis
  {
    path: ROAD_ANALYSIS_CONSTANTS.landing.route,
    sidebar: true,
    headerTitle: ROAD_ANALYSIS_CONSTANTS.landing.title,
  },
  {
    path: '*',
  },
]

/**
 * If you have an app route, add it here. This will populate in the table in the admin page. If you don't want it to
 * be in the admin table, like 'admin', then add it to the excluded list.
 *
 * WARNING: THIS DEFINES THE ORDER OF 'EDIT/ADD USER' TABLE ROWS, SO MAKE SURE YOU KEEP THE ORDER WE WANT.
 */
export enum EArgosRouteKeys {
  OBJECTS = 'Object Definition Database',

  // RWO
  POTENTIALLY_MOVING_OBJECTS = 'Potentially Moving Objects',
  MISCELLANEOUS_OBJECTS = 'Miscellaneous Objects',
  OBJECT_CLASSIFICATION = 'Object Classification',
  RWO_SIGNS = 'Sign Objects',
  RWO_SIGNS_PICKLIST = 'Sign Objects - Picklist Data',

  // Sensors
  SENSOR_RADAR = 'Radar Database',
  SENSOR_CAMERA = 'Camera Database',
  SENSOR_LIDAR = 'Lidar Database',
  SENSOR_ULTRASONIC = 'Ultrasonic Database',

  COVERAGE_REQUIREMENTS = 'Coverage Requirements',
  ADAS_PROGRAMS = 'ADAS Programs',
  SENSING_SYSTEM_ARCHITECTURE_DESIGN = 'Sensing System Architecture Design',
  VEHICLE_PROGRAMS = 'Vehicle Programs',
  SCENARIO_ANALYSIS = 'Scenario Analysis Models',
  ADMIN = 'admin',

  ROAD_ANALYSIS = 'Road Analysis',
}

export const ROUTES_EXCLUDED_FROM_ADMIN_TABLE = [EArgosRouteKeys.ADMIN, EArgosRouteKeys.OBJECTS]

const ROUTES_TO_PERMISSIONS: { [key: string]: EArgosRouteKeys } = {
  // TODO: Careful with cases where there is a dialog or new tab with different routes.

  // vehicle programs
  '/vehicleprogram': EArgosRouteKeys.VEHICLE_PROGRAMS,
  [VEHICLE_PROGRAMS_CONSTANTS.itemDetails.route]: EArgosRouteKeys.VEHICLE_PROGRAMS,

  // sensor radar
  '/sensorradar': EArgosRouteKeys.SENSOR_RADAR,
  '/radarPerformance': EArgosRouteKeys.SENSOR_RADAR,

  // sensor camera
  '/sensorcamera': EArgosRouteKeys.SENSOR_CAMERA,
  '/cameraPerformance': EArgosRouteKeys.SENSOR_CAMERA,

  // sensor lidar
  '/sensorlidar': EArgosRouteKeys.SENSOR_LIDAR,
  '/lidarPerformance': EArgosRouteKeys.SENSOR_LIDAR,

  // sensor ultrasonic
  '/sensorultrasonic': EArgosRouteKeys.SENSOR_ULTRASONIC,
  '/ultrasonicPerformance': EArgosRouteKeys.SENSOR_ULTRASONIC,


  // objects
  '/objects': EArgosRouteKeys.OBJECTS,
  '/objectDetails': EArgosRouteKeys.OBJECTS,

  // real world objects
  '/potentiallymovingobjects': EArgosRouteKeys.POTENTIALLY_MOVING_OBJECTS,
  '/potentiallymovingobjectdetails': EArgosRouteKeys.POTENTIALLY_MOVING_OBJECTS,
  '/objectclassification': EArgosRouteKeys.OBJECT_CLASSIFICATION,
  '/signs': EArgosRouteKeys.RWO_SIGNS,
  '/signsrwodetails': EArgosRouteKeys.RWO_SIGNS,
  '/signspicklist': EArgosRouteKeys.RWO_SIGNS_PICKLIST,
  '/miscellaneousObjects': EArgosRouteKeys.MISCELLANEOUS_OBJECTS,
  '/miscellaneousobjectsdetails': EArgosRouteKeys.MISCELLANEOUS_OBJECTS,

  /** Scenario Analysis */
  [SCENARIO_ANALYSIS_CONSTANTS.landing.route]: EArgosRouteKeys.SCENARIO_ANALYSIS,
  [SCENARIO_ANALYSIS_CONSTANTS.viewModel.route]: EArgosRouteKeys.SCENARIO_ANALYSIS,
  [SCENARIO_ANALYSIS_CONSTANTS.viewVariables.route]: EArgosRouteKeys.SCENARIO_ANALYSIS,

  /** Sensing System Architecture Design */
  [SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.landing.route]: EArgosRouteKeys.SENSING_SYSTEM_ARCHITECTURE_DESIGN,
  [SENSING_SYSTEM_ARCHITECTURE_DESIGN_CONSTANTS.viewStudy.route]: EArgosRouteKeys.SENSING_SYSTEM_ARCHITECTURE_DESIGN,

  // coverage requirements
  '/coverage-requirements': EArgosRouteKeys.COVERAGE_REQUIREMENTS,
  '/coveragerequirementsstudydetails': EArgosRouteKeys.COVERAGE_REQUIREMENTS,

  // adas programs
  '/adas-programs': EArgosRouteKeys.ADAS_PROGRAMS,
  '/adas-programs-details': EArgosRouteKeys.ADAS_PROGRAMS,

  '/admin': EArgosRouteKeys.ADMIN,

  [ROAD_ANALYSIS_CONSTANTS.landing.route]: EArgosRouteKeys.ROAD_ANALYSIS,
}
export {ARGOS_ROUTES, ROUTES_TO_PERMISSIONS}